import React from 'react';
import { useAuth } from '../../context/AuthContext'; 
import { useNavigate, useLocation, Link } from 'react-router-dom';
import UserOrdersPage from './OrdersPage'; 

// --- Content Components ---
const ProfileContent: React.FC<{ userName: string }> = ({ userName }) => (
    <div className="p-5 sm:p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 border-b pb-3">My Profile Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600">Name</label>
                <input 
                    type="text" 
                    defaultValue={userName} 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 transition outline-none"
                />
            </div>
            
            <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600">Email ID</label>
                 <input 
                    type="email" 
                    defaultValue="user.email@example.com"
                    readOnly
                    className="w-full p-3 border border-gray-200 bg-gray-100 rounded-lg text-gray-500 cursor-not-allowed"
                />
            </div>

            <div className="space-y-2">
                <label className="text-sm font-medium text-gray-600">Mobile No.</label>
                 <input 
                    type="tel" 
                    defaultValue="9876543210"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 transition outline-none"
                />
            </div>
        </div>

        <button className="mt-6 w-full sm:w-auto px-8 py-3 bg-yellow-600 text-white font-semibold rounded-lg shadow-md hover:bg-yellow-700 transition duration-300">
            Update Profile
        </button>
    </div>
);

const PlaceholderContent: React.FC<{ title: string }> = ({ title }) => (
    <div className="p-5 sm:p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-3">{title}</h3>
        <p className="text-gray-600">This section is currently under development.</p>
    </div>
);


// --- Main Page Component ---
const UserProfilePage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    if (!user) {
        navigate('/login'); 
        return null;
    }

    const userName = user.userName || 'User';
    const currentPath = location.pathname;

    // Navigation Links Data
    const navLinks = [
        { name: 'Profile Details', path: '/profile' },
        { name: 'My Orders', path: '/orders' },
        { name: 'Wishlist', path: '/wishlist' },
        { name: 'My Wallet', path: '/wallet' },
    ];
    
    const renderContent = () => {
        if (currentPath.startsWith('/orders')) return <UserOrdersPage />;
        if (currentPath.startsWith('/wishlist')) return <PlaceholderContent title="My Wishlist" />;
        if (currentPath.startsWith('/wallet')) return <PlaceholderContent title="My Wallet" />;
        return <ProfileContent userName={userName} />;
    };
    
    return (
        <main className="min-h-screen bg-gray-50 py-6 sm:py-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">My Account</h1>
                
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    
                    {/* --- Responsive Sidebar Navigation --- */}
                    <aside className="lg:w-1/4 w-full shrink-0">
                        {/* Mobile: Horizontal scrollable tabs. Desktop: Vertical List */}
                        <nav 
                          aria-label="Account Navigation" 
                          className="flex lg:flex-col overflow-x-auto lg:overflow-visible gap-2 pb-2 lg:pb-0 scrollbar-hide bg-white lg:bg-transparent p-2 lg:p-0 rounded-lg lg:rounded-none border lg:border-none border-gray-200 shadow-sm lg:shadow-none"
                        >
                            {navLinks.map((link) => {
                                // Match path logic
                                const isActive = currentPath === link.path || (currentPath === '/profile' && link.path === '/profile');
                                
                                return (
                                    <Link
                                        key={link.name}
                                        to={link.path}
                                        className={`whitespace-nowrap px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                                            isActive 
                                              ? 'bg-yellow-50 text-yellow-700 border-l-0 lg:border-l-4 lg:border-yellow-600' 
                                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                        }`}
                                    >
                                        {link.name}
                                    </Link>
                                );
                            })}
                        </nav>
                    </aside>
                    
                    {/* --- Content Area --- */}
                    <section className="lg:w-3/4 w-full">
                        {renderContent()} 
                    </section>
                    
                </div>
            </div>
        </main>
    );
};

export default UserProfilePage;